import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { useLatest } from 'react-use';
import { useBiLogger } from '../../../Provider/BiLogger/BiLoggerProvider';
import { type OwnProps } from '../types';
import { useSelector } from '../../../../store';
import { selectTopProps } from '../../../../selectors';
import { selectPayPalPaymentData } from '../../../../selectors/payment';
import { selectPayPalMetadata } from '../../../../selectors/domain';
import { amountToLong } from '../../../../utils/amountToLong';
import { type OrderItem, type PaymentBreakdown } from '../../../../types';
import { getBIErrorDescription } from '../../../../utils/bi/getBIErrorDescription';
import { type PayPalOnShippingChangeParams } from '../../../../types/paypal';
import { perfNow } from '../../../../utils/perfNow';
import {
  ecwGettingPaymentDataFromVertical,
  fastCheckoutButtonClicked,
  paymentModalClosed,
  spbButtonRendered,
  spbButtonStartInitializationCheckout,
  spbButtonSuccessInitializationCheckout,
  spbButtonTrxAuthorized,
  spbCountryMissed,
  spbBuyerMissedShippingOrBillingAddressFields,
  spbErrorDuringStartInitializationCheckout,
  spbErrorDuringValidationFromVertical,
  spbShippingMethodChanged,
  spbShippingMethodNotAccepted,
  spbOnPaymentAuthorizedCallbackResult,
} from '../../../../utils/bi/events/biEvents';
import { type PayPalFundingSource } from '../../../../types/PaymentsButtonViewConfig';

export const useBI = ({ fundingSource, place }: OwnProps) => {
  const biLogger = useBiLogger();
  const { meta, currency, paymentAmount, loadInfo } = useSelector(selectTopProps);
  const { appInstanceId, cartId, productId } = meta;
  const paymentDataRef = useLatest(useSelector(selectPayPalPaymentData));
  const methodMetadata = useSelector(selectPayPalMetadata);
  const paymentProvider = methodMetadata.providerId;
  const amount = amountToLong(paymentAmount);

  return {
    reportModalClosed: (data: { orderID: string }) =>
      biLogger.report(paymentModalClosed, {
        paymentProvider,
        cartId,
        paymentmethod: fundingSource,
        externalOrderId: data.orderID,
        transactionId: paymentDataRef.current.chargeId,
        orderSnapshotId: paymentDataRef.current.cashierOrderId,
        amount,
        currency,
        isPartner: undefined as any, // TODO[bi]
        checkoutType: undefined as any, // TODO[bi]
        pageType: undefined as any, // TODO[bi]
      }),

    reportFastCheckoutButtonClicked: (data: { fundingSource: PayPalFundingSource }) =>
      biLogger.report(fastCheckoutButtonClicked, {
        paymentProvider,
        cartId,
        paymentmethod: data.fundingSource,
        productid: productId,
        currency,
        amount,
        isPartner: undefined as any, // TODO[bi]
        transactionId: undefined as any, // TODO[bi]
        orderSnapshotId: undefined as any, // TODO[bi]
      }),

    reportErrorDuringValidation: (data: { fundingSource: PayPalFundingSource }) =>
      biLogger.report(spbErrorDuringValidationFromVertical, {
        paymentProvider,
        cartId,
        productId,
        paymentmethod: data.fundingSource,
        currency,
        amount,
        transactionId: undefined as any, // TODO[bi]
        orderSnapshotId: undefined as any, // TODO[bi]
        isPartner: undefined as any, // TODO[bi]
        errorReason: undefined as any, // TODO[bi]
      }),

    reportInitCheckoutStart: () =>
      biLogger.report(spbButtonStartInitializationCheckout, {
        paymentmethod: fundingSource,
        paymentProvider,
        cartId,
        amount,
        currency,
        isPartner: undefined as any, // TODO[bi]
        transactionId: undefined as any, // TODO[bi]
        orderSnapshotId: undefined as any, // TODO[bi]
      }),

    reportInitCheckoutSuccess: (data: {
      orderId: string;
      chargeId: string;
      cashierOrderId: string;
      paymentAmount: string;
    }) =>
      biLogger.report(spbButtonSuccessInitializationCheckout, {
        paymentProvider,
        amount: amountToLong(data.paymentAmount),
        ecToken: data.orderId,
        paymentmethod: fundingSource,
        cartId,
        transactionId: data.chargeId,
        orderSnapshotId: data.cashierOrderId,
        currency,
        isPartner: undefined as any, // TODO[bi]
      }),

    reportInitCheckoutFailure: (data: { error: any; paymentAmount: string }) =>
      biLogger.report(spbErrorDuringStartInitializationCheckout, {
        paymentProvider,
        amount: amountToLong(data.paymentAmount),
        currency,
        errorReason: data.error?.response?.data?.message || data.error?.message,
        ...getBIErrorDescription(data.error),
        paymentmethod: fundingSource,
        cartId,
        productId,
        isPartner: undefined as any, // TODO[bi]
        transactionId: undefined as any, // TODO[bi]
        orderSnapshotId: undefined as any, // TODO[bi]
      }),

    reportGettingPaymentDataFromVerticalStart: () =>
      biLogger.report(ecwGettingPaymentDataFromVertical, {
        paymentProvider,
        amount,
        stepName: 'initialization',
        paymentmethod: fundingSource,
        currency,
        cartId,
        productid: productId,
        isPartner: undefined as any, // TODO[bi]
        transactionId: undefined as any, // TODO[bi]
        orderSnapshotId: undefined as any, // TODO[bi]
        errorReason: undefined as any, // TODO[bi]
        result: undefined as any, // TODO[bi]
        taxAmount: undefined as any, // TODO[bi]
        shippingAmount: undefined as any, // TODO[bi]
        orderType: undefined as any, // TODO[bi]
        discountAmount: undefined as any, // TODO[bi]
      }),

    reportGettingPaymentDataFromVerticalSuccess: (data: {
      paymentBreakdown: PaymentBreakdown;
      orderItems: OrderItem[];
      paymentAmount: string;
    }) =>
      biLogger.report(ecwGettingPaymentDataFromVertical, {
        paymentProvider,
        amount: amountToLong(data.paymentAmount),
        stepName: 'result',
        result: 'success',
        currency,
        paymentmethod: fundingSource,
        shippingAmount: amountToLong(data.paymentBreakdown.shipping),
        taxAmount: amountToLong(data.paymentBreakdown.tax),
        discountAmount: amountToLong(data.paymentBreakdown.discount),
        orderType: data.orderItems.map((o) => o.category).join(),
        cartId,
        productid: productId,
        isPartner: undefined as any, // TODO[bi]
        transactionId: undefined as any, // TODO[bi]
        orderSnapshotId: undefined as any, // TODO[bi]
        errorReason: undefined as any, // TODO[bi]
      }),

    reportGettingPaymentDataFromVerticalFailure: (data: { error: Error }) =>
      biLogger.report(ecwGettingPaymentDataFromVertical, {
        paymentProvider,
        stepName: 'result',
        result: 'failure',
        paymentmethod: fundingSource,
        errorReason: data.error.message,
        cartId,
        currency,
        amount,
        productid: productId,
        isPartner: undefined as any, // TODO[bi]
        transactionId: undefined as any, // TODO[bi]
        orderSnapshotId: undefined as any, // TODO[bi]
        taxAmount: undefined as any, // TODO[bi]
        shippingAmount: undefined as any, // TODO[bi]
        orderType: undefined as any, // TODO[bi]
        discountAmount: undefined as any, // TODO[bi]
      }),

    reportShippingMethodChanged: (data: PayPalOnShippingChangeParams) =>
      biLogger.report(spbShippingMethodChanged, {
        paymentProvider,
        appInstanceId,
        paymentMethod: PaymentMethod.PayPal,
        externalOrderId: data.orderID,
        amountLong: amountToLong(data.amount.value),
        currency: data.amount.currency_code,
        transactionId: paymentDataRef.current.chargeId,
        orderSnapshotId: paymentDataRef.current.cashierOrderId,
        amount,
        isPartner: undefined as any, // TODO[bi]
        fieldChanged: undefined as any, // TODO[bi]
      }),

    reportShippingMethodNotAccepted: (data: {
      params: PayPalOnShippingChangeParams;
      errorReason: string;
      amountLong?: number;
    }) =>
      biLogger.report(spbShippingMethodNotAccepted, {
        paymentProvider,
        appInstanceId,
        currency: data.params.amount.currency_code,
        externalOrderId: data.params.orderID,
        paymentMethod: PaymentMethod.PayPal,
        transactionId: paymentDataRef.current.chargeId,
        orderSnapshotId: paymentDataRef.current.cashierOrderId,
        errorReason: data.errorReason,
        ...(typeof data.amountLong !== 'undefined' && { amountLong: data.amountLong }),
        amount,
        isPartner: undefined as any, // TODO[bi]
        fieldChanged: undefined as any, // TODO[bi]
      }),

    reportTrxAuthorized: (data: { orderID: string }) =>
      biLogger.report(spbButtonTrxAuthorized, {
        paymentProvider,
        paymentmethod: fundingSource,
        transactionId: paymentDataRef.current.chargeId,
        orderSnapshotId: paymentDataRef.current.cashierOrderId,
        cartId,
        externalOrderId: data.orderID,
        currency,
        amount,
        isPartner: undefined as any, // TODO[bi]
      }),

    reportCountryMissed: (data: { orderID: string }) =>
      biLogger.report(spbCountryMissed, {
        paymentProvider,
        paymentMethod: PaymentMethod.PayPal,
        appInstanceId,
        externalOrderId: data.orderID,
        currency,
        transactionId: paymentDataRef.current.chargeId,
        orderSnapshotId: paymentDataRef.current.cashierOrderId,
        amount,
        isPartner: undefined as any, // TODO[bi]
      }),

    reportBuyerMissedShippingOrBillingAddressFields: (data: {
      orderID: string;
      emptyBillingFields: string;
      emptyShippingFields: string;
    }) =>
      biLogger.report(spbBuyerMissedShippingOrBillingAddressFields, {
        paymentProvider,
        paymentMethod: PaymentMethod.PayPal,
        appInstanceId,
        externalOrderId: data.orderID,
        emptyBillingFields: data.emptyBillingFields,
        emptyShippingFields: data.emptyShippingFields,
        amount,
        currency,
        transactionId: paymentDataRef.current.chargeId,
        orderSnapshotId: paymentDataRef.current.cashierOrderId,
        isPartner: undefined as any, // TODO[bi]
      }),

    reportOnPaymentAuthorizedCallbackResult: (data: { orderID: string; result: string }) =>
      biLogger.report(spbOnPaymentAuthorizedCallbackResult, {
        amount,
        currency,
        externalOrderId: data.orderID,
        paymentmethod: PaymentMethod.PayPal,
        paymentProvider,
        result: data.result,
        transactionId: paymentDataRef.current.chargeId,
        orderSnapshotId: paymentDataRef.current.cashierOrderId,
        cartId,
        isPartner: undefined as any, // TODO[bi]
      }),

    reportButtonRendered: () =>
      biLogger.report(spbButtonRendered, {
        paymentProvider,
        renderPlace: place,
        paymentmethod: fundingSource,
        loadTime: Math.floor(perfNow() - loadInfo.loadStartTime),
        cartId,
        productId,
        currency,
        amount,
        isPartner: undefined as any, // TODO[bi]
        orderSnapshotId: undefined as any, // TODO[bi]
        transactionId: undefined as any, // TODO[bi]
      }),
  };
};
